import Icon from '@components/v2/ui/Icon/Icon';
import Loading from '@components/v2/ui/Loading/Loading';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { memo } from 'react';
import { makeStyles } from 'tss-react/mui';
import ConversionRing from '../../../components/ConversionRing';
import useWidth from '../../../components/Hooks/useWidth';
import useMoney from '../../../hooks/useMoney';
import LangEnum from '../../../lang/LangEnum';
import {
  SalesOpportunity,
  SalesOpportunityConversion,
} from '../../sales-opportunity/salesOpportunity.types';
import { useTranslation } from '../../translation/TranslationContext';

interface ISalesOpportunityCardProps {
  salesOpportunity: SalesOpportunity;
  reducedSize?: boolean;
  isSelected: boolean;
  conversionValues?: SalesOpportunityConversion;
  testId: string;
  loadingConversion: boolean;
  handleClickCard: () => void;
}

interface ICardProps {
  isSelected: boolean;
}

const useStyles = makeStyles<ICardProps>()((theme, { isSelected }) => ({
  card: {
    height: '29px',
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    border: `0.5px solid ${theme.palette.custom.border.primary}`,
    backgroundColor: isSelected ? theme.palette.background.default : 'transparent',
  },
  container: {
    display: 'flex',
    columnGap: theme.spacing(0.5),
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${theme.spacing(0.5)} ${theme.spacing(0.75)}`,
  },
  text: {
    minWidth: '61px',
    textAlign: 'right',
  },
  chevron: {
    color: theme.palette.text.secondary,
  },
}));

const SalesOpportunityCard = ({
  salesOpportunity,
  reducedSize,
  isSelected,
  conversionValues,
  testId,
  loadingConversion,
  handleClickCard,
}: ISalesOpportunityCardProps) => {
  const { classes, cx } = useStyles({ isSelected });
  const { mobileWidth } = useWidth();
  const { formatAbreviatedMoney } = useMoney();
  const { translate } = useTranslation();

  const reducedSizeValue = formatAbreviatedMoney(
    conversionValues ? conversionValues.achievedValue : salesOpportunity.achievedValue,
  );

  const fullSizeValue = `${translate(LangEnum.GLOBAL_FROM_VALUE, {
    minValue: formatAbreviatedMoney(
      conversionValues ? conversionValues.achievedValue : salesOpportunity.achievedValue,
    ),
    maxValue: formatAbreviatedMoney(
      conversionValues ? conversionValues.opportunityValue : salesOpportunity.value,
    ),
  })}`;

  return (
    <Box
      className={cx(classes.container, classes.card)}
      onClick={handleClickCard}
      test-id={testId}
      sx={{
        maxWidth: mobileWidth ? '100%' : '370px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {loadingConversion ? (
          <Loading size={20} />
        ) : (
          <ConversionRing
            value={conversionValues ? conversionValues.conversion : salesOpportunity.conversion}
            size={20}
            showTextRightSide
            horizontalSpacing={6}
            thickness={10}
          />
        )}

        <Typography variant="body2" color="textSecondary" className={classes.text}>
          {reducedSize ? reducedSizeValue : fullSizeValue}
        </Typography>
      </Box>
      <Icon icon={isSelected ? ChevronDown : ChevronRight} size="md" className={classes.chevron} />
    </Box>
  );
};

export default memo(SalesOpportunityCard);
